/**
 * Observations API Service
 * Handles API calls for observations submissions
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with auth interceptor
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Type definitions
export interface VehicleObservation {
  id?: number;
  observation_id?: number;
  vehicle_number: number;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_color?: string;
  distinguishing_features?: string;
  latitude: number;
  longitude: number;
  location_name_address?: string;
  observation_datetime?: string;
  created_at?: string;
}

export interface IndividualObservation {
  id?: number;
  observation_id?: number;
  individual_number: number;
  gender?: string;
  race?: string;
  age_group?: string;
  body_build?: string;
  characteristics?: string;
  latitude: number;
  longitude: number;
  location_name_address?: string;
  observation_datetime?: string;
  created_at?: string;
}

export interface Observation {
  id?: number;
  observation_date: string;
  student_number: string;
  detachment: string;
  area_observances?: string;
  narrative_description?: string;
  // Cadre feedback (set via updateCadreNotes; read-only for students)
  cadre_notes?: string | null;
  cadre_notes_by?: string | null;
  cadre_notes_at?: string | null;
  vehicle_observations: VehicleObservation[];
  individual_observations: IndividualObservation[];
  created_at?: string;
  updated_at?: string;
}

export interface ObservationListResponse {
  items: Observation[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

/**
 * Create a new observation submission
 */
export const createObservation = async (observation: Observation): Promise<Observation> => {
  try {
    const response = await axiosInstance.post<Observation>(
      '/observations/',
      observation
    );
    return response.data;
  } catch (error) {
    console.error('Error creating observation:', error);
    throw error;
  }
};

/**
 * Get all observations with pagination and optional filtering
 */
export const getObservations = async (
  page: number = 1,
  pageSize: number = 50,
  studentNumber?: string
): Promise<ObservationListResponse> => {
  try {
    const params: any = { page, page_size: pageSize };
    if (studentNumber) {
      params.student_number = studentNumber;
    }
    
    const response = await axiosInstance.get<ObservationListResponse>(
      '/observations/',
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching observations:', error);
    throw error;
  }
};

/**
 * Get a specific observation by ID
 */
export const getObservation = async (observationId: number): Promise<Observation> => {
  try {
    const response = await axiosInstance.get<Observation>(
      `/observations/${observationId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching observation:', error);
    throw error;
  }
};

/**
 * Update an existing observation
 */
export const updateObservation = async (
  observationId: number,
  updates: Partial<Observation>
): Promise<Observation> => {
  try {
    const response = await axiosInstance.put<Observation>(
      `/observations/${observationId}`,
      updates
    );
    return response.data;
  } catch (error) {
    console.error('Error updating observation:', error);
    throw error;
  }
};

/**
 * Update only the cadre notes on an observation.
 * Pass `null` or an empty string to clear the note.
 * Backend rejects the call for student roles (HTTP 403).
 */
export const updateCadreNotes = async (
  observationId: number,
  cadreNotes: string | null
): Promise<Observation> => {
  try {
    const response = await axiosInstance.patch<Observation>(
      `/observations/${observationId}/cadre-notes`,
      { cadre_notes: cadreNotes }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating cadre notes:', error);
    throw error;
  }
};

/**
 * Delete an observation
 */
export const deleteObservation = async (observationId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/observations/${observationId}`);
  } catch (error) {
    console.error('Error deleting observation:', error);
    throw error;
  }
};

